/**
 * Forum Component - Interactive forum with categories and discussion threads
 */
import React, { useState, useEffect } from 'react';
import { AuthContext } from '../../WebApp';
import axios from 'axios';

function Forum() {
  const { user, language } = React.useContext(AuthContext);
  const [view, setView] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newThreadDescription, setNewThreadDescription] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [threadsByCategory, setThreadsByCategory] = useState({});
  const [cooldowns, setCooldowns] = useState({ thread: 0, post: 0 });
  const [selectedCategoryForThread, setSelectedCategoryForThread] = useState(null);
  const [avatarModalUser, setAvatarModalUser] = useState(null); // User whose avatar is being changed
  const [avatarUrl, setAvatarUrl] = useState(''); // Avatar URL input

  const translations = {
    en: {
      title: 'Forum',
      subtitle: 'Community discussions and topics',
      categories: 'Categories',
      threads: 'Threads',
      posts: 'Posts',
      views: 'Views',
      createThread: 'Create New Thread',
      createPost: 'Reply',
      newThread: 'New Discussion',
      cancel: 'Cancel',
      create: 'Create',
      post: 'Post',
      back: 'Back',
      loading: 'Loading...',
      error: 'Error',
      noThreads: 'No threads in this category yet',
      noPosts: 'No posts yet',
      loginRequired: 'Sign in to participate',
      threadTitle: 'Title',
      threadDescription: 'Description',
      postContent: 'Your message',
      createdBy: 'Started by',
      cooldown: 'You can post again in',
      seconds: 'seconds',
      minutes: 'minutes',
      category: 'Category',
      selectCategory: 'Select a category',
      deleteThread: 'Delete thread',
      deletePost: 'Delete post',
      deleteConfirmThread: 'Delete this thread with all messages?',
      deleteConfirmPost: 'Delete this message?',
      categoryNames: {
        'General Discussion': 'General Discussion',
        'Feature Requests': 'Feature Requests',
        'Bug Reports': 'Bug Reports',
        'Character Events': 'Character Events',
        'Tips & Guides': 'Tips & Guides',
        'Bonuses': 'Bonuses'
      },
      categoryDescriptions: {
        'General Discussion': 'General topics and off-topic chat',
        'Feature Requests': 'Suggest new features for HotBump',
        'Bug Reports': 'Report bugs and technical issues',
        'Character Events': 'Discuss character storylines and events',
        'Tips & Guides': 'Share tips, guides, and tutorials',
        'Bonuses': 'Free tokens, promotions and special offers'
      }
    },
    ru: {
      title: 'Форум',
      subtitle: 'Обсуждения и темы сообщества',
      categories: 'Категории',
      threads: 'Темы',
      posts: 'Сообщения',
      views: 'Просмотров',
      createThread: 'Новая тема',
      createPost: 'Ответить',
      newThread: 'Новое обсуждение',
      cancel: 'Отмена',
      create: 'Создать',
      post: 'Отправить',
      back: 'Назад',
      loading: 'Загрузка...',
      error: 'Ошибка',
      noThreads: 'Нет тем в этой категории',
      noPosts: 'Нет сообщений',
      loginRequired: 'Войдите чтобы участвовать',
      threadTitle: 'Заголовок',
      threadDescription: 'Описание',
      postContent: 'Ваше сообщение',
      createdBy: 'Автор',
      cooldown: 'Можно опубликовать через',
      seconds: 'секунд',
      minutes: 'минут',
      category: 'Категория',
      selectCategory: 'Выберите категорию',
      deleteThread: 'Удалить тему',
      deletePost: 'Удалить сообщение',
      deleteConfirmThread: 'Удалить эту тему со всеми сообщениями?',
      deleteConfirmPost: 'Удалить это сообщение?',
      categoryNames: {
        'General Discussion': 'Общее обсуждение',
        'Feature Requests': 'Предложения',
        'Bug Reports': 'Баги и ошибки',
        'Character Events': 'События персонажей',
        'Tips & Guides': 'Советы и гайды',
        'Bonuses': 'Бонусы'
      },
      categoryDescriptions: {
        'General Discussion': 'Общие темы и оффтоп',
        'Feature Requests': 'Предложите новые функции для HotBump',
        'Bug Reports': 'Сообщайте о багах и технических проблемах',
        'Character Events': 'Обсуждение сюжетных линий персонажей',
        'Tips & Guides': 'Делитесь советами, гайдами и туториалами',
        'Bonuses': 'Бесплатные токены, промо и спецпредложения'
      }
    }
  };

  const t = translations[language] || translations.en;
  const isForumAdmin = user?.nickname?.toLowerCase() === 'mad';

  const getCategoryDisplayName = (category) => {
    if (!category) return '';
    return t.categoryNames[category.name] || category.name || category.name_ru || category.name_en || '';
  };

  // Filter threads based on language
  const filterThreadsByLanguage = (threads) => {
    if (!threads) return threads;
    return threads.filter(thread => {
      // Show Rules thread only in English, Правила only in Russian
      if (thread.title === 'Rules' && language !== 'en') return false;
      if (thread.title === 'Правила' && language !== 'ru') return false;
      return true;
    });
  };

  // Navigate to Bonuses category
  const goToBonuses = () => {
    const bonusesCategory = categories.find(c => c.name === 'Bonuses');
    if (bonusesCategory) {
      fetchCategoryThreads(bonusesCategory.id);
      // Scroll to category after short delay
      setTimeout(() => {
        const element = document.getElementById(`category-${bonusesCategory.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  // Cooldown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCooldowns(prev => {
        const newCooldowns = { ...prev };
        if (newCooldowns.thread > 0) newCooldowns.thread--;
        if (newCooldowns.post > 0) newCooldowns.post--;
        return newCooldowns;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  const formatCooldown = (seconds) => {
    if (seconds < 60) {
      return `${seconds} ${t.seconds}`;
    }
    const mins = Math.ceil(seconds / 60);
    return `${mins} ${t.minutes}`;
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/web/forum/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryThreads = async (categoryId) => {
    try {
      if (threadsByCategory[categoryId]) {
        setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
        return;
      }
      
      const response = await axios.get(`/web/forum/categories/${categoryId}/threads`);
      setThreadsByCategory(prev => ({
        ...prev,
        [categoryId]: response.data.threads
      }));
      setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
    } catch (err) {
      console.error('Error fetching threads:', err);
      setError(t.error);
    }
  };

  const fetchThreadDetail = async (threadId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/web/forum/threads/${threadId}`);
      setSelectedThread(response.data);
      setView('thread-detail');
    } catch (err) {
      console.error('Error fetching thread:', err);
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!user) {
      setError(t.loginRequired);
      return;
    }
    if (!selectedCategoryForThread) {
      setError(t.selectCategory || 'Please select a category');
      return;
    }
    if (cooldowns.thread > 0) {
      setError(`${t.cooldown} ${formatCooldown(cooldowns.thread)}`);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await axios.post(`/web/forum/categories/${selectedCategoryForThread}/threads`, {
        title: newThreadTitle,
        description: newThreadDescription
      });
      setNewThreadTitle('');
      setNewThreadDescription('');
      setSelectedCategoryForThread(null);
      setCooldowns(prev => ({ ...prev, thread: 1800 })); // 30 minutes
      setView('categories');
      setThreadsByCategory({});
      await fetchCategories();
    } catch (err) {
      if (err.response?.status === 429) {
        setError(err.response.data.detail);
      } else {
        console.error('Error creating thread:', err);
        setError(t.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!user) {
      setError(t.loginRequired);
      return;
    }
    if (cooldowns.post > 0) {
      setError(`${t.cooldown} ${formatCooldown(cooldowns.post)}`);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await axios.post(`/web/forum/threads/${selectedThread.id}/posts`, {
        content: newPostContent
      });
      setNewPostContent('');
      setCooldowns(prev => ({ ...prev, post: 240 })); // 4 minutes
      await fetchThreadDetail(selectedThread.id);
    } catch (err) {
      if (err.response?.status === 429) {
        setError(err.response.data.detail);
      } else {
        console.error('Error creating post:', err);
        setError(t.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAvatar = async (e) => {
    e.preventDefault();
    if (!avatarUrl || !user) return;
    
    try {
      const response = await axios.post('/api/update_avatar', {
        avatar_url: avatarUrl
      });
      
      if (response.data.success) {
        // Close modal and refresh thread detail if open
        setAvatarModalUser(null);
        setAvatarUrl('');
        if (view === 'thread-detail' && selectedThread) {
          await fetchThreadDetail(selectedThread.id);
        }
      }
    } catch (err) {
      console.error('Error updating avatar:', err);
      alert(err.response?.data?.detail || 'Error updating avatar');
    }
  };

  const handleDeleteThread = async (threadId) => {
    if (!isForumAdmin) return;
    if (!window.confirm(t.deleteConfirmThread)) return;

    try {
      setLoading(true);
      setError(null);
      await axios.delete(`/web/forum/threads/${threadId}`);

      if (view === 'thread-detail' && selectedThread?.id === threadId) {
        setSelectedThread(null);
        setView('categories');
      }

      setThreadsByCategory({});
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting thread:', err);
      setError(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!isForumAdmin || !selectedThread?.id) return;
    if (!window.confirm(t.deleteConfirmPost)) return;

    try {
      setLoading(true);
      setError(null);
      await axios.delete(`/web/forum/posts/${postId}`);
      await fetchThreadDetail(selectedThread.id);
    } catch (err) {
      console.error('Error deleting post:', err);
      setError(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  // Categories view
  if (view === 'categories') {
    return (
      <div className="w-full bg-dark-900 min-h-screen py-8">
        <div className="px-4 sm:px-6 lg:px-8 w-full">
          {/* Header with Title and Create Button */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">{t.title}</h1>
              <p className="text-gray-400">{t.subtitle}</p>
            </div>
            {user && (
              <button
                onClick={() => {
                  setView('create-thread');
                  setSelectedCategoryForThread(null);
                  setNewThreadTitle('');
                  setNewThreadDescription('');
                }}
                className="bg-burgundy-600 hover:bg-burgundy-500 text-white px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2"
              >
                <span className="text-xl">+</span>
                <span className="hidden sm:inline">{t.createThread}</span>
              </button>
            )}
          </div>

          {/* Welcome Banner */}
          <div 
            onClick={goToBonuses}
            className="mb-8 bg-gradient-to-r from-burgundy-600 to-burgundy-500 rounded-lg p-6 cursor-pointer hover:from-burgundy-500 hover:to-burgundy-400 transition-all transform hover:scale-[1.02] shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  {language === 'ru' 
                    ? '🎉 Форум HotBump приветствует Вас!' 
                    : '🎉 Forum HotBump welcomes you!'}
                </h2>
                <p className="text-white/90 text-lg">
                  {language === 'ru' 
                    ? 'Забери бесплатные 50 токенов!' 
                    : 'Claim 50 free tokens!'}
                </p>
              </div>
              <div className="text-5xl">🎁</div>
            </div>
          </div>

          {error && <div className="bg-red-900 text-red-200 p-4 rounded mb-6">{error}</div>}

          {loading && !categories.length ? (
            <div className="text-center py-8 text-gray-400">{t.loading}</div>
          ) : (
            <div className="space-y-2">
              {categories.map(category => (
                <div key={category.id} id={`category-${category.id}`} className="bg-dark-800 rounded-lg overflow-hidden border border-dark-700">
                  {/* Category Header */}
                  <button
                    onClick={() => fetchCategoryThreads(category.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-dark-700 transition text-left"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <span className="text-2xl">{category.icon}</span>
                      <div>
                        <h3 className="text-lg font-bold text-white">
                          {t.categoryNames[category.name] || category.name}
                        </h3>
                        <p className="text-sm text-gray-400">
                          {t.categoryDescriptions[category.name] || category.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-gray-400">
                      <span className="text-sm">{category.thread_count} {t.threads}</span>
                      <span className={`text-xl transition-transform ${expandedCategory === category.id ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </button>

                  {/* Threads List */}
                  {expandedCategory === category.id && threadsByCategory[category.id] && (
                    <div className="border-t border-dark-700 bg-dark-900/50">
                      {filterThreadsByLanguage(threadsByCategory[category.id]).length === 0 ? (
                        <div className="px-6 py-4 text-gray-400 text-sm">{t.noThreads}</div>
                      ) : (
                        <div className="divide-y divide-dark-700">
                          {filterThreadsByLanguage(threadsByCategory[category.id]).map(thread => (
                            <div
                              key={thread.id}
                              className="w-full px-6 py-4 hover:bg-dark-800/50 transition flex items-center justify-between gap-4"
                            >
                              <button
                                onClick={() => fetchThreadDetail(thread.id)}
                                className="flex-1 text-left"
                              >
                                <div className="flex items-start gap-2 mb-1">
                                  {thread.is_pinned && <span title="Pinned">📌</span>}
                                  <h4 className="text-white font-semibold">{thread.title}</h4>
                                </div>
                                <p className="text-sm text-gray-400 mb-2">{thread.description}</p>
                                <div className="flex gap-4 text-xs text-gray-500">
                                  <span>{t.createdBy} {thread.author?.nickname}</span>
                                  <span>•</span>
                                  <span>{thread.post_count} {t.posts}</span>
                                  <span>•</span>
                                  <span>{thread.views} {t.views}</span>
                                </div>
                              </button>
                              {isForumAdmin && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteThread(thread.id)}
                                  className="px-3 py-1 text-xs bg-red-900 hover:bg-red-800 text-red-200 rounded"
                                  title={t.deleteThread}
                                >
                                  🗑
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Create thread view
  if (view === 'create-thread') {
    return (
      <div className="w-full bg-dark-900 min-h-screen py-8">
        <div className="px-4 sm:px-6 lg:px-8 w-full">
          <button
            className="text-burgundy-400 hover:text-burgundy-300 mb-6"
            onClick={() => setView('categories')}
          >
            ← {t.categories}
          </button>

          <div className="bg-dark-800 rounded-lg p-8 border border-dark-700">
            <h2 className="text-3xl font-bold text-white mb-8">{t.newThread}</h2>
            {error && <div className="bg-red-900 text-red-200 p-4 rounded mb-6">{error}</div>}
            
            <form onSubmit={handleCreateThread}>
              <div className="mb-6">
                <label className="block text-white mb-2 font-semibold">{t.category || 'Category'}</label>
                <select
                  value={selectedCategoryForThread || ''}
                  onChange={(e) => setSelectedCategoryForThread(parseInt(e.target.value))}
                  className="w-full bg-dark-700 text-white px-4 py-3 rounded border border-dark-600 focus:border-burgundy-600 focus:outline-none"
                  required
                >
                  <option value="">-- {t.selectCategory || 'Select a category'} --</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {getCategoryDisplayName(cat)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-white mb-2 font-semibold">{t.threadTitle}</label>
                <input
                  type="text"
                  value={newThreadTitle}
                  onChange={(e) => setNewThreadTitle(e.target.value)}
                  placeholder={t.threadTitle}
                  className="w-full bg-dark-700 text-white px-4 py-3 rounded border border-dark-600 focus:border-burgundy-600 focus:outline-none"
                  required
                />
              </div>
              <div className="mb-8">
                <label className="block text-white mb-2 font-semibold">{t.threadDescription}</label>
                <textarea
                  value={newThreadDescription}
                  onChange={(e) => setNewThreadDescription(e.target.value)}
                  placeholder={t.threadDescription}
                  rows={6}
                  className="w-full bg-dark-700 text-white px-4 py-3 rounded border border-dark-600 focus:border-burgundy-600 focus:outline-none"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setView('categories')}
                  className="px-8 py-3 bg-dark-700 text-white rounded hover:bg-dark-600 transition font-semibold"
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={loading || cooldowns.thread > 0}
                  className="px-8 py-3 bg-burgundy-600 text-white rounded hover:bg-burgundy-500 transition font-semibold disabled:opacity-50"
                >
                  {cooldowns.thread > 0 ? `${formatCooldown(cooldowns.thread)}` : t.create}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Thread detail view
  if (view === 'thread-detail' && selectedThread) {
    return (
      <div className="w-full bg-dark-900 min-h-screen py-8">
        <div className="px-2 sm:px-4 lg:px-8 w-full">
          <div className="max-w-6xl mx-auto mb-6">
            <button
              className="text-burgundy-400 hover:text-burgundy-300"
              onClick={() => {
                setView('categories');
                setSelectedThread(null);
              }}
            >
              ← {t.categories}
            </button>
          </div>

          {error && <div className="bg-red-900 text-red-200 p-4 rounded mb-6 max-w-6xl mx-auto">{error}</div>}

          {/* Thread Header */}
          <div className="bg-dark-800 rounded-lg p-8 mb-8 border border-dark-700 max-w-6xl mx-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h2 className="text-4xl font-bold text-white">{selectedThread.title}</h2>
              {isForumAdmin && (
                <button
                  type="button"
                  onClick={() => handleDeleteThread(selectedThread.id)}
                  className="px-4 py-2 bg-red-900 hover:bg-red-800 text-red-200 rounded text-sm"
                >
                  {t.deleteThread}
                </button>
              )}
            </div>
            <p className="text-gray-300 text-lg mb-4">{selectedThread.description}</p>
            <div className="flex gap-6 text-sm text-gray-400">
              <span>👤 {selectedThread.author?.nickname}</span>
              <span>👁️ {selectedThread.views} {t.views}</span>
              <span>💬 {selectedThread.posts?.length} {t.posts}</span>
            </div>
          </div>

          {/* Posts */}
          <div className="space-y-4 mb-8">
            {selectedThread.posts?.length === 0 ? (
              <div className="text-center py-8 text-gray-400">{t.noPosts}</div>
            ) : (
              selectedThread.posts?.map(post => (
                <div key={post.id} className="bg-dark-800 rounded-lg border border-dark-700 overflow-hidden">
                  <div className="flex gap-0">
                    {/* User Info Column */}
                    <div className="w-40 bg-dark-900/50 p-4 border-r border-dark-700 flex flex-col items-center text-center flex-shrink-0">
                      {/* User Avatar */}
                      <div
                        onClick={() => {
                          // Allow changing avatar only for current user
                          if (user && post.author?.id === user.id) {
                            setAvatarModalUser(post.author);
                            setAvatarUrl(post.author?.avatar_url || '');
                          }
                        }}
                        className={`w-12 h-12 rounded-full mb-3 flex items-center justify-center text-white font-bold flex-shrink-0 
                          ${post.author?.avatar_url ? 'bg-cover bg-center' : 'bg-gradient-to-br from-burgundy-600 to-burgundy-800'}
                          ${user && post.author?.id === user.id ? 'cursor-pointer hover:opacity-80 transition ring-2 ring-burgundy-500' : ''}`}
                        style={post.author?.avatar_url ? { backgroundImage: `url(${post.author.avatar_url})` } : {}}
                      >
                        {!post.author?.avatar_url && post.author?.nickname?.charAt(0).toUpperCase()}
                      </div>
                      
                      {/* Username */}
                      <strong className="text-white text-sm font-semibold mb-2 break-words">
                        {post.author?.nickname}
                      </strong>
                      
                      {/* User Stats */}
                      <div className="w-full text-xs text-gray-400 space-y-1">
                        <div className="text-gray-500">⭐ {post.author?.reputation || 0}</div>
                        <div className="text-gray-500">📊 {post.author?.total_score || 0}</div>
                      </div>
                      
                      {/* Post Date */}
                      <span className="text-gray-500 text-xs mt-3 pt-3 border-t border-dark-700 w-full">
                        {new Date(post.created_at).toLocaleDateString(language === 'ru' ? 'ru-RU' : 'en-US')}
                      </span>
                    </div>
                    
                    {/* Post Content Column */}
                    <div className="flex-1 p-6">
                      {isForumAdmin && (
                        <div className="flex justify-end mb-2">
                          <button
                            type="button"
                            onClick={() => handleDeletePost(post.id)}
                            className="px-3 py-1 text-xs bg-red-900 hover:bg-red-800 text-red-200 rounded"
                          >
                            {t.deletePost}
                          </button>
                        </div>
                      )}
                      <p className="text-gray-200 whitespace-pre-wrap break-words leading-relaxed">{post.content}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Reply Form */}
          {user ? (
            <div className="max-w-6xl mx-auto bg-dark-800 rounded-lg p-8 border border-dark-700">
              <h3 className="text-2xl font-bold text-white mb-6">{t.createPost}</h3>
              <form onSubmit={handleCreatePost}>
                <textarea
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  placeholder={t.postContent}
                  rows={5}
                  className="w-full bg-dark-700 text-white px-4 py-3 rounded border border-dark-600 focus:border-burgundy-600 focus:outline-none mb-4"
                  required
                />
                <button
                  type="submit"
                  disabled={loading || cooldowns.post > 0}
                  className="px-8 py-3 bg-burgundy-600 text-white rounded hover:bg-burgundy-500 transition font-semibold disabled:opacity-50"
                >
                  {cooldowns.post > 0 ? `${formatCooldown(cooldowns.post)}` : t.post}
                </button>
              </form>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto bg-dark-800 rounded-lg p-8 text-center border border-dark-700">
              <p className="text-gray-400">{t.loginRequired}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Avatar Modal
  if (avatarModalUser) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-dark-800 rounded-lg p-8 max-w-md w-full border border-dark-700">
          <h3 className="text-2xl font-bold text-white mb-6">Cambiar Avatar / Изменить аватарку</h3>
          <form onSubmit={handleUpdateAvatar}>
            <div className="mb-6">
              <label className="block text-white mb-2 font-semibold">Avatar URL:</label>
              <input
                type="url"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                placeholder="https://example.com/avatar.jpg"
                className="w-full bg-dark-700 text-white px-4 py-3 rounded border border-dark-600 focus:border-burgundy-600 focus:outline-none"
                required
              />
              <p className="text-xs text-gray-400 mt-2">Enter full image URL (PNG, JPG, GIF)</p>
            </div>
            
            {avatarUrl && (
              <div className="mb-6">
                <p className="text-white text-sm mb-2">Preview:</p>
                <img 
                  src={avatarUrl} 
                  alt="Avatar preview" 
                  className="w-24 h-24 rounded-full mx-auto object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    alert('Invalid image URL');
                  }}
                />
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setAvatarModalUser(null);
                  setAvatarUrl('');
                }}
                className="flex-1 px-4 py-3 bg-dark-700 text-white rounded hover:bg-dark-600 transition font-semibold"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={loading || !avatarUrl}
                className="flex-1 px-4 py-3 bg-burgundy-600 text-white rounded hover:bg-burgundy-500 transition font-semibold disabled:opacity-50"
              >
                {loading ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return null;
}

export default Forum;
